import React, { useState } from 'react';
import { Plus, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { apiPost, apiDel } from '../../lib/api.js';
import { US_STATES, US_TIMEZONES } from '../../lib/usConstants.js';
import { Modal, Input, Select, parseCsvLine } from './leadsConstants.jsx';

export function CreateLeadModal({ vendors = [], onClose, onCreated }) {
  const [form, setForm] = useState({
    firstName: '', lastName: '', email: '', phone: '',
    state: '', city: '', zip: '', timezone: '',
    productInterest: '', priority: 'WARM', vendor: '',
  });
  const [saving, setSaving] = useState(false);
  const [attest, setAttest] = useState(false);

  const ATTEST_TEXT = 'I attest these leads have valid TCPA/express-written consent. CIVIX stamps the audit trail; the agent bears compliance responsibility.';

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = attest
        ? { ...form, tcpaConsent: { given: true, source: 'manual', manualAttestation: true, text: ATTEST_TEXT } }
        : form;
      await apiPost('/leads', payload);
      toast.success('Lead created');
      onCreated?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Create failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title="Create Lead" onClose={onClose}>
      <form onSubmit={submit} className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Input label="First name" value={form.firstName} onChange={(v) => setForm({ ...form, firstName: v })} required />
          <Input label="Last name" value={form.lastName} onChange={(v) => setForm({ ...form, lastName: v })} />
          <Input label="Phone *" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} required />
          <Input label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <Select label="State" value={form.state} onChange={(v) => setForm({ ...form, state: v })}>
            <option value="">—</option>
            {US_STATES.map((s) => <option key={s.code} value={s.code}>{s.code}</option>)}
          </Select>
          <Input label="City" value={form.city} onChange={(v) => setForm({ ...form, city: v })} />
          <Input label="ZIP" value={form.zip} onChange={(v) => setForm({ ...form, zip: v })} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Select label="Time zone" value={form.timezone} onChange={(v) => setForm({ ...form, timezone: v })}>
            <option value="">—</option>
            {US_TIMEZONES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
          </Select>
          <Select label="Vendor" value={form.vendor} onChange={(v) => setForm({ ...form, vendor: v })}>
            <option value="">—</option>
            {vendors.map((v) => <option key={v._id} value={v._id}>{v.name}</option>)}
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Input label="Product interest" value={form.productInterest} onChange={(v) => setForm({ ...form, productInterest: v })} />
          <Select label="Priority" value={form.priority} onChange={(v) => setForm({ ...form, priority: v })}>
            <option value="HOT">Hot</option>
            <option value="WARM">Warm</option>
            <option value="COLD">Cold</option>
          </Select>
        </div>
        <label className="flex items-start gap-2 text-sm rounded-sm border border-amber-300 bg-amber-50 p-3">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={attest}
            onChange={(e) => setAttest(e.target.checked)}
          />
          <span className="text-amber-900">{ATTEST_TEXT} <span className="font-medium">Enables AI texting for this lead.</span></span>
        </label>
        <div className="flex justify-end gap-2 pt-3">
          <button type="button" onClick={onClose} className="btn-ghost">Cancel</button>
          <button type="submit" disabled={saving} className="btn-primary">{saving ? 'Saving…' : 'Create lead'}</button>
        </div>
      </form>
    </Modal>
  );
}

export function UploadCsvModal({ vendors = [], onClose, onUploaded }) {
  const [file, setFile] = useState(null);
  const [vendor, setVendor] = useState('');
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState([]);

  const handleFile = (f) => {
    setFile(f);
    const reader = new FileReader();
    reader.onload = () => {
      const text = reader.result;
      const lines = String(text).split(/\r?\n/).slice(0, 6).filter(Boolean);
      setPreview(lines);
    };
    reader.readAsText(f);
  };

  const upload = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const text = await file.text();
      const [headerLine, ...rows] = text.split(/\r?\n/).filter(Boolean);
      const headers = headerLine.split(',').map((h) => h.trim().toLowerCase());
      const leads = rows.map((line) => {
        const cells = parseCsvLine(line);
        const o = {};
        headers.forEach((h, i) => (o[h] = cells[i]));
        return {
          firstName: o.firstname || o['first name'] || o.first || '',
          lastName: o.lastname || o['last name'] || o.last || '',
          email: o.email || '',
          phone: o.phone || o['phone number'] || o.cell || '',
          state: (o.state || '').toUpperCase().slice(0, 2),
          city: o.city || '',
          zip: o.zip || o.zipcode || '',
          timezone: o.timezone || o['time zone'] || '',
          productInterest: o.product || o['product interest'] || '',
          vendor: vendor || undefined,
        };
      });
      const r = await apiPost('/leads/bulk', { leads });
      toast.success(`Imported ${r.count} leads`);
      onUploaded?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Upload failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Upload CSV" onClose={onClose}>
      <div className="space-y-3">
        <p className="text-xs text-white/60">
          Required column: <code className="bg-white/10 px-1 rounded-sm">phone</code>. Recommended:
          <code className="bg-white/10 px-1 rounded-sm mx-1">firstName</code>
          <code className="bg-white/10 px-1 rounded-sm mr-1">lastName</code>
          <code className="bg-white/10 px-1 rounded-sm mr-1">email</code>
          <code className="bg-white/10 px-1 rounded-sm mr-1">state</code>
          <code className="bg-white/10 px-1 rounded-sm">timezone</code>.
        </p>
        <Select label="Tag with vendor" value={vendor} onChange={setVendor}>
          <option value="">—</option>
          {vendors.map((v) => <option key={v._id} value={v._id}>{v.name}</option>)}
        </Select>
        <input
          type="file"
          accept=".csv,text/csv"
          onChange={(e) => handleFile(e.target.files?.[0])}
          className="block w-full text-sm text-white/80 file:mr-3 file:px-3 file:py-2 file:rounded-sm file:border-0 file:bg-civix-purple file:text-white"
        />
        {preview.length > 0 && (
          <div className="bg-black/30 rounded-sm p-2 text-[11px] font-mono whitespace-pre overflow-auto max-h-32 border border-white/10">
            {preview.join('\n')}
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="btn-ghost">Cancel</button>
          <button onClick={upload} disabled={!file || busy} className="btn-primary">
            {busy ? 'Uploading…' : 'Import leads'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

export function ManageDispositionsModal({ dispositions = [], onClose, onChange }) {
  const [label, setLabel] = useState('');
  const [color, setColor] = useState('#8b5cf6');
  const [systemCode, setSystemCode] = useState('');
  const [busy, setBusy] = useState(false);

  const add = async () => {
    if (!label.trim()) return;
    setBusy(true);
    try {
      await apiPost('/dispositions', { label, color, systemCode: systemCode || null });
      setLabel(''); setColor('#8b5cf6'); setSystemCode('');
      onChange?.();
      toast.success('Disposition added');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Add failed');
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this disposition?')) return;
    try {
      await apiDel(`/dispositions/${id}`);
      onChange?.();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Delete failed');
    }
  };

  return (
    <Modal title="Manage Dispositions" onClose={onClose} wide>
      <div className="space-y-4">
        <div>
          <h4 className="text-xs uppercase tracking-wider text-white/50 mb-2">Add custom disposition</h4>
          <div className="flex flex-wrap items-end gap-2">
            <Input label="Label" value={label} onChange={setLabel} />
            <div>
              <label className="block text-[10px] uppercase tracking-wider text-white/40 mb-1">Color</label>
              <input
                type="color"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                className="w-12 h-9 rounded-sm border border-white/10 bg-white/5"
              />
            </div>
            <Select label="Maps to system code" value={systemCode} onChange={setSystemCode}>
              <option value="">— none (informational tag)</option>
              <option value="SOLD">SOLD (closed-won)</option>
              <option value="NO_SALE">NO_SALE</option>
              <option value="NO_SHOW">NO_SHOW</option>
              <option value="BOOKED_APPOINTMENT">BOOKED_APPOINTMENT</option>
              <option value="NOT_INTERESTED">NOT_INTERESTED</option>
              <option value="DNC">DNC</option>
              <option value="QUOTED">QUOTED</option>
              <option value="CALL_BACK">CALL_BACK</option>
              <option value="FOLLOW_UP">FOLLOW_UP</option>
              <option value="OPT_OUT">OPT_OUT</option>
            </Select>
            <button disabled={busy} onClick={add} className="btn-primary">
              <Plus className="w-4 h-4" /> Add
            </button>
          </div>
        </div>
        <div>
          <h4 className="text-xs uppercase tracking-wider text-white/50 mb-2">All dispositions ({dispositions.length})</h4>
          <div className="grid grid-cols-2 gap-2">
            {dispositions.map((d) => (
              <div
                key={d._id}
                className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-lg px-3 py-2"
              >
                <span className="w-3 h-3 rounded-full shrink-0" style={{ background: d.color }} />
                <span className="flex-1 text-sm">{d.label}</span>
                {d.isStandard ? (
                  <span className="text-[10px] uppercase tracking-wider text-white/40">Standard</span>
                ) : (
                  <button
                    onClick={() => remove(d._id)}
                    className="text-white/40 hover:text-red-400"
                  ><X className="w-4 h-4" /></button>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </Modal>
  );
}
